/**
 * ProjectOrchestrator - Level 1 Orchestrator
 *
 * RESPONSIBILITY:
 * Manages multiple configurations and coordinates high-level processing flow.
 *
 * HIERARCHY:
 * - Reports to: UI Store (useProjectStore or similar)
 * - Manages: ConfigurationManager, DisplayCoordinator
 * - Peers: None (top-level orchestrator)
 *
 * KEY OPERATIONS:
 * - createConfiguration: Creates new configuration when setup changes
 * - manageConfigurations: Lifecycle management and cleanup
 * - coordinateProcessing: Triggers processing for specific configurations
 *
 * NAVIGATION:
 * - For configuration creation logic, see: ./configuration-manager.ts
 * - For display coordination, see: ./display-coordinator.ts
 */

import { buildFirstFitCandidates } from '~/builders/candidate-builder';
import { ConfigurationProcessor } from '~/engine/processing/configuration-processor';
import { logger } from '~/services/logging';
import type { BondSeries, Configuration, Project } from '~/types';
import { ConfigurationManager } from './configuration-manager';
import { DisplayCoordinator } from './display-coordinator';

export interface OrchestrationContext {
  scenarioId?: string;
  actionId?: string;
}

export class ProjectOrchestrator {
  private configurationManager: ConfigurationManager;
  private displayCoordinator: DisplayCoordinator;

  constructor() {
    this.configurationManager = new ConfigurationManager();
    this.displayCoordinator = new DisplayCoordinator();
  }

  /**
   * Creates a new configuration with the provided setup
   */
  createConfiguration(
    project: Project,
    allBondSeries: BondSeries[],
    context?: OrchestrationContext
  ): Configuration {
    logger.L1.debug(`Creating configuration for ${allBondSeries.length} bond series`, {
      bondSeriesCount: allBondSeries.length,
      scenarioId: context?.scenarioId,
    });

    const currentConfigurations = project.configurations || [];
    const isFirstConfiguration = currentConfigurations.length === 0;

    // Determine whether to create a new configuration
    const shouldCreate = this.configurationManager.shouldCreateConfiguration(
      project,
      allBondSeries,
      isFirstConfiguration
    );

    if (!shouldCreate) {
      logger.L1.debug('No configuration creation needed - setup unchanged');
      return currentConfigurations[currentConfigurations.length - 1];
    }

    // Create the new configuration
    // Calculate the next orderId by finding the highest existing orderId
    const highestOrderId = currentConfigurations.reduce(
      (max, config) => Math.max(max, config.orderId || 0),
      0
    );

    const newConfiguration = this.configurationManager.createConfiguration(
      project,
      allBondSeries,
      highestOrderId, // This will be incremented by 1 inside createConfiguration
      context
    );

    logger.L1.info(`Configuration created: ${newConfiguration.id}`, {
      configurationId: newConfiguration.id,
      orderId: newConfiguration.orderId,
    });

    return newConfiguration;
  }

  /**
   * Manages configuration lifecycle, including cleanup of old configurations
   */
  manageConfigurations(
    configurations: Configuration[],
    maxConfigurations: number = 3
  ): Configuration[] {
    return this.configurationManager.cleanupOldConfigurations(configurations, maxConfigurations);
  }

  /**
   * Coordinates processing for a specific configuration
   */
  async coordinateProcessing(
    project: Project,
    configurationId: string,
    processingType: 'firstFit' | 'bestFit' | 'complete' | 'optimize'
  ): Promise<Configuration | null> {
    const configuration = project.configurations?.find((config) => config.id === configurationId);
    if (!configuration) {
      logger.L1.error(`Configuration not found: ${configurationId}`);
      return null;
    }

    logger.L1.info(`Coordinating ${processingType} processing`, {
      processingType,
      configurationId,
    });

    try {
      // Update status
      switch (processingType) {
        case 'firstFit':
          return this.processFirstFit(project, configuration);
        case 'bestFit':
          return await this.processBestFit(project, configuration);
        case 'complete':
          return await this.processComplete(project, configuration);
        case 'optimize':
          // Optimization will be handled in later phases
          logger.L1.info('Optimization processing not yet implemented');
          return configuration;
        default:
          throw new Error(`Unknown processing type: ${processingType}`);
      }
    } catch (error) {
      logger.L1.error(`Processing failed for configuration ${configurationId}`, {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
      return configuration;
    }
  }

  /**
   * Process first-fit candidates for a configuration
   */
  private processFirstFit(project: Project, configuration: Configuration): Configuration {
    // Generate candidates
    const candidates = buildFirstFitCandidates(project, configuration);

    // Update configuration with candidates
    configuration.candidates = candidates;
    configuration.totalCandidates = candidates.length;

    logger.L1.info(`Generated ${candidates.length} first-fit candidates`, {
      candidateCount: candidates.length,
      configurationId: configuration.id,
    });

    return configuration;
  }

  /**
   * Process best-fit candidates for a configuration
   */
  private async processBestFit(
    project: Project,
    configuration: Configuration
  ): Promise<Configuration> {
    if (!project.dateElements || !project.taxableValueMap || !configuration.candidates?.length) {
      logger.L1.warn('Missing required data for best-fit processing', {
        hasDateElements: !!project.dateElements,
        hasTaxableValueMap: !!project.taxableValueMap,
        candidateCount: configuration.candidates?.length || 0,
      });
      return configuration;
    }

    const baseElements = {
      dateRecords: project.dateElements.dateRecords || [],
      dateRecordsMapByTaxYear: project.dateElements.dateRecordsMapByTaxYear!,
      dateRecordsMapByPaymentDate: project.dateElements.dateRecordsMapByPaymentDate!,
      taxableValueMap: project.taxableValueMap,
      existingDebtInformation: {
        totalExistingDebt: 0,
        existingDebtProjects: project.existingDebtProjects || [],
        combinedDebtService: project.existingDebtProjects?.[0] || {
          id: 'default',
          name: 'Default',
          existingDebtPaymentRecords: [],
          totalPrincipal: 0,
          totalInterest: 0,
          totalPayments: 0,
        },
      },
    };

    // Use the static processConfiguration method instead of processBestFit
    const result = await ConfigurationProcessor.processConfiguration(
      configuration,
      project,
      baseElements,
      {
        useIterativeShapeSearch: true,
        maxShapesToTry: 50,
        enableAutomaticOptimization: false,
        enableLogging: true,
      }
    );

    return result.success ? result.configuration : configuration;
  }

  /**
   * Process complete configuration (first-fit + best-fit + optimization)
   */
  private async processComplete(
    project: Project,
    configuration: Configuration
  ): Promise<Configuration> {
    // Process first-fit
    let processedConfiguration = this.processFirstFit(project, configuration);

    // Then process best-fit
    processedConfiguration = await this.processBestFit(project, processedConfiguration);

    // Optimization will be added in later phases

    return processedConfiguration;
  }

  /**
   * Determines how to display results from multiple configurations
   */
  getDisplayRecommendation(configurations: Configuration[]): {
    primaryConfiguration: Configuration | null;
    comparisonConfigurations: Configuration[];
    displayMode: 'single' | 'comparison' | 'progression';
  } {
    return this.displayCoordinator.getDisplayRecommendation(configurations);
  }

  /**
   * Gets processing statistics across all configurations
   */
  getProcessingStats(configurations: Configuration[]): {
    totalConfigurations: number;
    processedConfigurations: number;
    totalCandidates: number;
    viableCandidates: number;
  } {
    const processedConfigurations = configurations.filter(
      (config) => config.candidates && config.candidates.length > 0
    );

    const stats = {
      totalConfigurations: configurations.length,
      processedConfigurations: processedConfigurations.length,
      totalCandidates: 0,
      viableCandidates: 0,
    };

    for (const config of processedConfigurations) {
      stats.totalCandidates += config.totalCandidates || 0;
      stats.viableCandidates += config.candidates?.filter((c) => c.results?.isViable).length || 0;
    }

    return stats;
  }
}
